import * as Sentry from '@sentry/angular';
import { Component, OnDestroy, signal } from '@angular/core'; // Corrected import order
import {
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import SpeedTest, { Results } from '@cloudflare/speedtest';
import { CommonModule } from '@angular/common';
import { LanguageSettingDialogComponent } from '../language-setting-dialog/language-setting-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ToastrService } from 'ngx-toastr';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-improve-audio-dialog',
  standalone: true,  imports: [
    MatDialogModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatProgressBarModule,
    CommonModule,
    TranslocoPipe
  ],
  templateUrl: './improve-audio-dialog.component.html',
  styleUrl: './improve-audio-dialog.component.scss',
})
export class ImproveAudioDialogComponent implements OnDestroy {
  public readonly uploadSpeedThreshold = 30 // MBPS
  public readonly latencyThreshold = 50; // MS
  private readonly speedTestTimeoutDuration = 120000; // 2 minute in milliseconds

  inAppBrowser = !!(
    window['cordova_iab'] ?? window['webkit']?.messageHandlers['cordova_iab']
  );

  isSpeedtestRunning = signal<boolean>(false);
  speedtestResults: {
    upload: number | null;
    latency: number | null;
    error: string | null;
  } = {
    upload: null,
    latency: null,
    error: null
  };
  private speedTestTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private speedTest: SpeedTest | null = null;

  constructor(private dialog: MatDialog, private toastr: ToastrService) {}

  openLanguageDialog() {
    this.dialog.open(LanguageSettingDialogComponent);
  }

  openMicrophoneGuide() {
    if (this.inAppBrowser) {
      const target =
        window['cordova_iab'] ??
        window['webkit']?.messageHandlers['cordova_iab'];
      target?.postMessage(JSON.stringify({ command: 'open-external-url', url: 'https://support.speak-see.com/en/how-can-i-improve-the-accuracy-of-transcriptions-with-the-microphone-kit' }));
    } else {
      window.open('https://support.speak-see.com/en/how-can-i-improve-the-accuracy-of-transcriptions-with-the-microphone-kit', '_blank');
    }
  }

  runSpeedtest() {
    if (this.speedTest) {
      return;
    }
    this.clearSpeedTestTimeout();

    this.speedtestResults = { upload: null, latency: null, error: null };
    this.isSpeedtestRunning.set(true);
    this.speedTest = null;

    this.speedTest = new SpeedTest({
      measurements: [
        { type: 'latency', numPackets: 1 },
        { type: 'latency', numPackets: 15 },
        { type: 'upload', bytes: 1e5, count: 3, },
        { type: 'upload', bytes: 2.5e7, count: 2 },
      ]
    });

    this.speedTestTimeoutId = setTimeout(() => {
      if (this.isSpeedtestRunning() && this.speedTest) {
        this.speedTest.pause();
        this.isSpeedtestRunning.set(false);
        this.speedTestTimeoutId = null;
        this.speedTest = null;

        this.speedtestResults = {
          upload: 1,
          latency: 100000,
          error: null
        };
      }
    }, this.speedTestTimeoutDuration);

    if (!this.speedTest) return;

    this.speedTest.play();

    this.speedTest.onError = (error) => {
      this.speedTestOnError(error);
    }

    this.speedTest.onFinish = (results) => {
      this.speedTestOnFinished(results);
    }
  }

  private speedTestOnFinished(results: Results) {
    this.clearSpeedTestTimeout();

    if (this.isSpeedtestRunning()) {
      const summary = results.getSummary();
      this.isSpeedtestRunning.set(false);
      this.speedtestResults = {
        upload: summary.upload ? summary.upload / 1000 / 1000 : 0,
        latency: summary.latency || 0,
        error: null
      };
    }
    this.speedTest = null;
  }

  private speedTestOnError(error: string) {
    this.clearSpeedTestTimeout();

    if (this.isSpeedtestRunning()) {
      this.isSpeedtestRunning.set(false);
      this.speedtestResults.error = 'Failed to complete speed test. Please try again later.';
      console.error('Speed test error:', error);
      Sentry.captureException(new Error(`Speed test error`), {
        extra: { error },
      });
      this.toastr.error(
        'Failed to complete speed test. Please check your connection and try again.',
        'Speed Test Error',
        { timeOut: 5000 }
      );
    }
    this.speedTest = null;
  }

  private clearSpeedTestTimeout() {
    if (this.speedTestTimeoutId) {
      clearTimeout(this.speedTestTimeoutId);
      this.speedTestTimeoutId = null;
    }
  }

  ngOnDestroy() {
    if (this.speedTestTimeoutId) {
      clearTimeout(this.speedTestTimeoutId);
      this.speedTestTimeoutId = null;
    }
    if (this.speedTest && this.isSpeedtestRunning()) {
      this.speedTest.pause();
      this.isSpeedtestRunning.set(false);
      console.log('Speed test paused due to component destruction.');
    }
    this.speedTest = null;
  }
}
